import {WriteButton} from '../common/buttons/WriteActions';
import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Divider, InputAdornment, Snackbar, Stack, TextField} from '@mui/material';
import {getMedia} from '../../common/mediaQuery';
import {fillingAPI} from "../../services/FillingServices";
import {FillingsTable} from "./filling/FillingTable";
import AddIcon from "@mui/icons-material/Add";
import {useNavigate} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import {Clear} from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import useDebounce from "../../hooks/useDebounce";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {usePageLimit} from "../../hooks/usePageLimit";
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

function Fillings() {
    const navigate = useNavigate();
    const {isFullHD} = getMedia();
    const {t} = useAppTranslation();

    const [createFilling, setCreateFilling] = useState(false);
    const [isNewDataFetching, setIsNewDataFetching] = useState(false);
    const [isAllFillingsIsLoading, setIsAllFillingsIsLoading] = useState(false);

    // api data state
    const [page, setPage] = useState(0);
    const [storedLimit, setLimit] = usePageLimit('fillings');
    const limit = storedLimit >= 1 && storedLimit <= 200 ? storedLimit : 15;
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');

    // search state
    const [searchPhrase, setSearchPhrase] = useState('');

    const [orderByName, setOrderByName] = useState(true);
    const [orderByPrice, setOrderByPrice] = useState(false);
    const [descending, setDescending] = useState(false);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const debouncedSearchPhrase = useDebounce(searchPhrase, 600);
    const {
        data: response,
        error,
        isLoading,
        isSuccess,
    } = fillingAPI.useGetFillingsV2Query({
        page,
        pageSize: limit,
        searchPhrase: debouncedSearchPhrase.trim().length >= 3
            ? debouncedSearchPhrase.trim()
            : "",
        descending,
    })

    const handlePageChange = (newPage: number) => setPage(newPage);

    const handlePageSizeChange = (newSize: number) => {
        setLimit(newSize);
        setPage(0);
    };

    const handleSortRequest = (
        id: 'name' | 'price',
        direction: 'asc' | 'desc'
    ) => {
        setDescending(direction === 'desc');

        // Сбрасываем все флаги и включаем только нужный
        setOrderByName(id === 'name');
        setOrderByPrice(id === 'price');
    };

    useEffect(() => {
        if (storedLimit !== limit) setLimit(limit);
    }, [storedLimit, limit, setLimit]);

    useEffect(() => {
        if (!error || getApiErrorKind(error) !== 'server') return;
        setServerToast(getApiErrorMessage(error, t));
        setTraceId(getApiTraceId(error));
    }, [error, t]);

    useEffect(() => {
        if (response) {

            setPage(response.page);
            setLimit(response.pageSize);

            setIsNewDataFetching(false);

            setIsAllFillingsIsLoading(!isSuccess);
        }
    }, [response]);

    return (
        <Box sx={{width: '100%'}}>
            <Box sx={{mb: 2, display: 'flex', justifyContent: 'flex-center', alignItems: 'center'}}>
                <Stack direction="row" divider={<Divider flexItem/>} alignItems="center" justifyContent={'center'}
                       gap={'5px'}>
                    <WriteButton
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => navigate('new')}
                    >
                        {`${t('recipes:create_filling')}`}
                    </WriteButton>
                </Stack>
            </Box>

            {(!isSuccess && isLoading) && (
                <ContentSkeleton variant="list"/>
            )}

            {!createFilling && isSuccess && !isLoading && (
                <Paper sx={{width: '100%', overflow: 'hidden'}}>
                    <Box sx={{p: 2}}>
                        <TextField
                            label={t('recipes:search_fillings')}
                            variant="outlined"
                            focused
                            size="small"
                            fullWidth
                            value={searchPhrase}
                            onChange={(e) => {
                                setSearchPhrase(e.target.value);
                            }}
                            InputProps={{
                                endAdornment: searchPhrase ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchPhrase("");
                                            }}
                                        >
                                            <Clear/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Box>
                    <FillingsTable
                        data={response.data}
                        totalCount={response.totalCount ?? 0}
                        pageSize={limit}
                        currentPage={page}
                        descending={descending}
                        orderByName={orderByName}
                        orderByPrice={orderByPrice}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSortRequest={handleSortRequest}
                    />
                </Paper>)}
            {error && getApiErrorKind(error) !== 'server' && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}><Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert></Snackbar>
        </Box>
    );
}

export default Fillings;
